import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Clock, Hash, Key, Lock, Shield } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Skeleton } from "../../../components/ui/Skeleton";
import { Spinner } from "../../../components/ui/Spinner";
import { fetchWithAuth } from "../../../hooks/useAuth";

interface SecuritySettings {
  passwordMinLength: number;
  passwordRequireNumbers: boolean;
  passwordRequireSymbols: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  ipWhitelist: string[];
}

interface ProfanityCheckSettings {
  enabled: boolean;
}

export const SecuritySettingsTab = ({
  selectedTab,
}: {
  selectedTab: string;
}) => {
  const queryClient = useQueryClient();
  // Fetch security settings
  const {
    data: securitySettings,
    isLoading: isLoadingSecurity,
    isError: isErrorSecurity,
    error: errorSecurity,
    refetch: refetchSecurity,
  } = useQuery({
    queryKey: ["security-settings"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/settings/security");
      if (!response.ok)
        throw new Error("Eroare la încărcarea setărilor de securitate");
      return response.json() as Promise<SecuritySettings>;
    },
    enabled: selectedTab === "security",
  });

  // Fetch profanity check feature flag
  const {
    data: profanityCheckEnabled,
    isLoading: isLoadingProfanity,
    isError: isErrorProfanity,
    error: errorProfanity,
    refetch: refetchProfanity,
  } = useQuery<ProfanityCheckSettings>({
    queryKey: ["feature-profanity-check"],
    queryFn: async () => {
      const response = await fetchWithAuth(
        "/api/admin/settings/features/profanity-check"
      );
      if (!response.ok)
        throw new Error(
          "Eroare la încărcarea setării de verificare a limbajului"
        );
      return response.json() as Promise<ProfanityCheckSettings>;
    },
    enabled: selectedTab === "security",
  });

  // Update security settings mutation
  const updateSecuritySettings = useMutation({
    mutationFn: async (data: Partial<SecuritySettings>) => {
      const response = await fetchWithAuth("/api/admin/settings/security", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok)
        throw new Error("Eroare la actualizarea setărilor de securitate");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["security-settings"] });
      toast.success("Setări de securitate actualizate cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update profanity check feature flag
  const updateProfanityCheck = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await fetchWithAuth(
        "/api/admin/settings/features/profanity-check",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled }),
        }
      );
      if (!response.ok)
        throw new Error(
          "Eroare la actualizarea setării de verificare a limbajului"
        );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-profanity-check"] });
      toast.success("Setare verificare cuvinte obscene actualizată");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const isLoading = isLoadingSecurity || isLoadingProfanity;
  const isError = isErrorSecurity || isErrorProfanity;
  const errorMessage =
    (errorSecurity as Error)?.message ||
    (errorProfanity as Error)?.message ||
    "A apărut o eroare neașteptată.";

  if (isLoading) {
    return (
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Setări Securitate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Profanity check row skeleton */}
          <div className="mb-4">
            <Card className="p-3 rounded-lg bg-background flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-52" />
              </div>
              <Skeleton className="h-9 w-60" />
            </Card>
          </div>
          {/* Fields skeletons */}
          <div className="space-y-4">
            <div>
              <Skeleton className="h-4 w-56 mb-2" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div>
              <Skeleton className="h-4 w-64 mb-2" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div>
              <Skeleton className="h-4 w-72 mb-2" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div>
              <Skeleton className="h-4 w-44 mb-2" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Eroare la încărcarea setărilor de securitate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{errorMessage}</div>
            <Button
              variant="secondary"
              onClick={() => {
                refetchSecurity();
                refetchProfanity();
              }}
              className="ml-4"
            >
              Reîncearcă
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!securitySettings) return null;

  return (
    <Card className="">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Setări Securitate
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4">
          {profanityCheckEnabled?.enabled !== undefined && (
            <Card
              className="p-3 rounded-lg bg-background  flex items-center justify-between"
              key={profanityCheckEnabled.enabled.toString()}
            >
              <div className="flex items-center gap-2 text-foreground">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Verificare cuvinte obscene
                </span>
              </div>
              <Button
                type="button"
                size="lg"
                variant={
                  profanityCheckEnabled.enabled ? "destructive" : "default"
                }
                onClick={() =>
                  updateProfanityCheck.mutate(!profanityCheckEnabled.enabled)
                }
                disabled={updateProfanityCheck.isPending}
                className="min-w-[240px] text-black gap-2"
              >
                {updateProfanityCheck.isPending ? (
                  <>
                    <Spinner className="w-4 h-4" />
                    Se aplică...
                  </>
                ) : profanityCheckEnabled.enabled ? (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    Dezactivează verificarea
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Activează verificarea
                  </>
                )}
              </Button>
            </Card>
          )}
          <div>
            <label
              htmlFor="passwordMinLength"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Lock className="w-4 h-4" />
              Lungime Minimă Parolă
            </label>
            <Input
              id="passwordMinLength"
              type="number"
              value={securitySettings.passwordMinLength}
              onChange={(e) =>
                updateSecuritySettings.mutate({
                  passwordMinLength: Number(e.target.value),
                })
              }
              className="mt-1"
              placeholder="Introduceți lungimea minimă a parolei"
            />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
            <input
              type="checkbox"
              id="requireNumbers"
              checked={securitySettings.passwordRequireNumbers}
              onChange={(e) =>
                updateSecuritySettings.mutate({
                  passwordRequireNumbers: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="requireNumbers"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Hash className="w-4 h-4" />
              Necesită Numere în Parolă
            </label>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
            <input
              type="checkbox"
              id="requireSymbols"
              checked={securitySettings.passwordRequireSymbols}
              onChange={(e) =>
                updateSecuritySettings.mutate({
                  passwordRequireSymbols: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="requireSymbols"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Key className="w-4 h-4" />
              Necesită Simboluri în Parolă
            </label>
          </div>
          <div>
            <label
              htmlFor="sessionTimeout"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              Timeout Sesiune (minute)
            </label>
            <Input
              id="sessionTimeout"
              type="number"
              value={securitySettings.sessionTimeout}
              onChange={(e) =>
                updateSecuritySettings.mutate({
                  sessionTimeout: Number(e.target.value),
                })
              }
              className="mt-1"
              placeholder="Introduceți timeout-ul sesiunii"
            />
          </div>
          <div>
            <label
              htmlFor="maxLoginAttempts"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Încercări Maxime de Autentificare
            </label>
            <Input
              id="maxLoginAttempts"
              type="number"
              value={securitySettings.maxLoginAttempts}
              onChange={(e) =>
                updateSecuritySettings.mutate({
                  maxLoginAttempts: Number(e.target.value),
                })
              }
              className="mt-1"
              placeholder="Introduceți numărul maxim de încercări"
            />
          </div>
          <div>
            <label
              htmlFor="ipWhitelist"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              IP Whitelist
            </label>
            <Input
              id="ipWhitelist"
              value={securitySettings.ipWhitelist.join(", ")}
              onChange={(e) =>
                updateSecuritySettings.mutate({
                  ipWhitelist: e.target.value.split(",").map((ip) => ip.trim()),
                })
              }
              className="mt-1"
              placeholder="IP-uri separate prin virgulă"
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
